import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./Dashboard.css";

const UsersDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loginLogsMap, setLoginLogsMap] = useState({});
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageSize = 10;

  const fetchData = async (pageNum = 1) => {
    try {
      const response = await axios.get(
        `https://api.herracim.az/api/dashboard/user-dashboard?page=${pageNum}&pageSize=${pageSize}`,
        { withCredentials: true }
      );

      const fetchedUsers = response.data.users;
      const fetchedLogs = response.data.loginLogs || [];

      const updatedMap = pageNum === 1 ? {} : { ...loginLogsMap };
      fetchedLogs.forEach(log => {
        const email = log.email.toLowerCase();
        if (!updatedMap[email]) {
          updatedMap[email] = [];
        }
        const alreadyExists = updatedMap[email].some(existing => existing.loginTime === log.loginTime);
        if (!alreadyExists) {
          updatedMap[email].push(log);
        }
      });

      if (pageNum === 1) {
        setUsers(fetchedUsers);
      } else {
        setUsers(prev => [...prev, ...fetchedUsers]);
      }
      setLoginLogsMap(updatedMap);
      setTotalUsers(response.data.totalCount);
      setHasMore(fetchedUsers.length === pageSize);
    } catch (error) {
      console.error("İstifadəçi statistikası yüklənmədi:", error);
      toast.error("Xəta baş verdi!");
    } finally {
      setLoading(false);
    }
  };

  const toggleBanStatus = async (userId, isBanned) => {
    try {
      const res = await axios.post(
        `https://api.herracim.az/api/dashboard/toggle-ban/${userId}`,
        null,
        { withCredentials: true }
      );
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, isBanned: !isBanned } : user
        )
      );
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err?.response?.data || "Əməliyyat zamanı xəta baş verdi!");
    }
  };

  const handleConfirmAction = () => {
    if (confirmAction) {
      toggleBanStatus(confirmAction.userId, confirmAction.isBanned);
      setConfirmAction(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const loadMoreUsers = async () => {
    const nextPage = page + 1;
    setIsLoadingMore(true);
    setPage(nextPage);
    await fetchData(nextPage);
    setIsLoadingMore(false);
  };

  const toggleLogView = (userId) => {
    setExpandedUserId(prev => (prev === userId ? null : userId));
  };

  return (
    <div className="dashboard-container">
      <h2>📊 İstifadəçi Paneli</h2>
      <p><strong>Ümumi istifadəçi sayı:</strong> {totalUsers}</p>

      {loading ? (
        <p>Yüklənir...</p>
      ) : (
        <>
          <div className="dashboard-section">
            <h3>👥 İstifadəçilər</h3>
            <table className="user-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Ad Soyad</th>
                  <th>Email</th>
                  <th>Telefon</th>
                  <th>Balans</th>
                  <th>Satış</th>
                  <th>Qazanc</th>
                  <th>Platforma</th>
                  <th>Rollar</th>
                  <th>Qeydiyyat</th>
                  <th>Aktivlik</th>
                  <th>Şikayət</th>
                  <th>Status</th>
                  <th>Əməliyyat</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, i) => {
                  const userEmail = user.email.toLowerCase();
                  const userLogs = loginLogsMap[userEmail] || [];

                  return (
                    <React.Fragment key={user.id}>
                      <tr
                        className={user.roles.includes("Admin") ? "admin-row" : user.isBanned ? "banned-row" : "active-row"}
                        onClick={() => toggleLogView(user.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{i + 1}</td>
                        <td>{user.fullName}</td>
                        <td>{user.email}</td>
                        <td>{user.phoneNumber || "-"}</td>
                        <td>{user.balance?.toFixed(2) || "0.00"} AZN</td>
                        <td>{user.totalSales?.toFixed(2) || "0.00"} AZN</td>
                        <td>{user.sellerIncomeFromConfirmedSales?.toFixed(2) || "0.00"} AZN</td>
                        <td>{user.platformProfitFromUser?.toFixed(2) || "0.00"} AZN</td>
                        <td>{user.roles.join(", ")}</td>
                        <td>{user.registeredAt && new Date(user.registeredAt).getFullYear() > 1900 ? new Date(user.registeredAt).toLocaleString() : "-"}</td>
                        <td>{user.lastActivity ? new Date(user.lastActivity).toLocaleString() : "-"}</td>
                        <td>{user.hasComplaint ? "❌ Var" : "✅ Yoxdur"}</td>
                        <td>
                          {user.isBanned ? (
                            <span className="status-banned">🚫 Banlı</span>
                          ) : (
                            <span className="status-active">✅ Aktiv</span>
                          )}
                        </td>
                        <td>
                          {user.roles.includes("Admin") ? (
                            <span className="admin-badge">👑 Admin</span>
                          ) : (
                            <button
                              className={user.isBanned ? "activate-btn" : "ban-btn"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmAction({ userId: user.id, isBanned: user.isBanned });
                              }}
                            >
                              {user.isBanned ? "Aktiv Et" : "Ban Et"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedUserId === user.id && userLogs.length > 0 && (
                        <tr className="log-row">
                          <td colSpan="14">
                            <div className="log-details">
                              <h4>📥 Giriş Logları - {user.fullName}</h4>
                              <table className="log-table">
                                <thead>
                                  <tr>
                                    <th>IP</th>
                                    <th>Cihaz</th>
                                    <th>Yer</th>
                                    <th>Vaxt</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {userLogs.map((log, index) => (
                                    <tr key={index}>
                                      <td>{log.ipAddress === "::1" ? "127.0.0.1" : log.ipAddress}</td>
                                      <td>{log.deviceName || log.userAgent}</td>
                                      <td>{log.location || "-"}</td>
                                      <td>{new Date(log.loginTime).toLocaleString()}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="load-more-container">
              <button onClick={loadMoreUsers} className="load-more-btn">
                {isLoadingMore ? "Yüklənir..." : "Daha çox yüklə"}
              </button>
            </div>
          )}
        </>
      )}

      {confirmAction && (
        <div className="popup-overlay">
          <div className="popup">
            <h4>Təsdiqlə</h4>
            <p>Bu istifadəçini {confirmAction.isBanned ? "aktiv etmək" : "banlamaq"} istədiyinizə əminsiniz?</p>
            <button onClick={handleConfirmAction}>Bəli</button>
            <button onClick={() => setConfirmAction(null)}>Xeyr</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersDashboard;
